// PRD.327 — what a printout is FOR.
//
// A clinic configures ONE prescription print template and ONE settings record.
// The same sheet is used for two different jobs, and only one of them should
// carry the clinical narrative:
//
//   consultation_sheet — the full visit document: vitals, clinical note, Rx
//   prescription       — what the patient takes to a pharmacy: Rx only
//
// This is a RUNTIME view of the saved settings, never a second settings system
// and never a second template. Nothing here is persisted: the clinic's saved
// configuration is untouched, so switching purpose cannot silently reconfigure
// their consultation sheet.
//
// Import-free so it runs under the node:test harness.

import type { PrescriptionPrintSettingsUI } from './prescription-print-settings-logic.js';

export const PRESCRIPTION_PRINT_PURPOSES = ['consultation_sheet', 'prescription'] as const;
export type PrescriptionPrintPurpose = (typeof PRESCRIPTION_PRINT_PURPOSES)[number];

/**
 * Default purpose for callers that do not state one.
 *
 * `consultation_sheet` means "render exactly what the saved settings say",
 * which is what every pre-PRD.327 caller already did — so adding the parameter
 * changes no existing output.
 */
export const DEFAULT_PRESCRIPTION_PRINT_PURPOSE: PrescriptionPrintPurpose = 'consultation_sheet';

/**
 * The settings a given purpose should render with.
 *
 * `consultation_sheet` returns the settings UNCHANGED — including vitals and
 * clinical sections the clinic has turned off. Purpose never turns anything ON;
 * it only withholds.
 *
 * `prescription` suppresses:
 *   - Vitals, via the authoritative `showVitals` column;
 *   - the Clinical Notes MASTER, which by PRD.320's own rule suppresses every
 *     subsection with it — Findings, Investigations, Diagnosis and both
 *     Treatment sections — without this function needing to know they exist;
 *   - the Chief Complaint, via its own `showComplaint` column.
 *
 * Complaint needs suppressing SEPARATELY because only the Dental Structured
 * layout routes it through the clinical-note master. Every other template
 * renders `showComplaint` on its own, so without this a prescription printed on
 * a classic template would still carry the patient's presenting complaint — a
 * consultation detail on a sheet meant for a pharmacy.
 *
 * Medication content is untouched: the Rx table is not part of any switch.
 *
 * Returns a COPY. The caller's saved settings object is never mutated, which is
 * what guarantees printing cannot write back a configuration change.
 */
export function applyPrintPurpose(
  settings: PrescriptionPrintSettingsUI,
  purpose: PrescriptionPrintPurpose = DEFAULT_PRESCRIPTION_PRINT_PURPOSE,
): PrescriptionPrintSettingsUI {
  if (purpose !== 'prescription') return settings;

  return {
    ...settings,
    showVitals: false,
    showComplaint: false,
    sections: {
      ...settings.sections,
      // The master switch alone is enough; leaving the subsections at their
      // saved values keeps this a pure view, so a clinic that later prints a
      // consultation sheet still gets its own configuration back.
      clinicalNotes: false,
    },
  };
}

/** Human label for the print action that produces each purpose. */
export const PRESCRIPTION_PRINT_PURPOSE_LABELS: Record<PrescriptionPrintPurpose, string> = {
  consultation_sheet: 'Consultation Sheet',
  prescription: 'Prescription',
};
